import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useParams } from 'react-router-dom';
import './event.css';

const API_URL = import.meta.env.VITE_API_URL;

/* ===============================================================
   定数定義
   =============================================================== */

/** 日本語の曜日配列 */
const JWD = ['日', '月', '火', '水', '木', '金', '土'];

/* ===============================================================
   ヘルパー関数（日付・時刻フォーマット）
   =============================================================== */

/** 数値を2桁にゼロ埋め */
const pad2 = (n) => String(n).padStart(2, '0');

/**
 * Dateオブジェクトから 'YYYY/MM/DD' 形式の文字列を生成
 * @param {Date} d - Dateオブジェクト
 * @returns {string} - フォーマットされた日付文字列
 */
const fmtYMD = (d) => `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;

/**
 * Dateオブジェクトから 'YYYY/MM/DD(曜)' 形式の文字列を生成
 * @param {Date} d - Dateオブジェクト
 * @returns {string} - フォーマットされた日付文字列（曜日付き）
 */
const fmtYMDw = (d) => `${fmtYMD(d)}(${JWD[d.getDay()]})`;

/**
 * Dateオブジェクトから 'HH：MM' 形式の文字列を生成（全角コロン、表示用）
 * @param {Date} d - Dateオブジェクト
 * @returns {string} - フォーマットされた時刻文字列
 */
const fmtHMJP = (d) => `${pad2(d.getHours())}：${pad2(d.getMinutes())}`;

/* ===============================================================
   ヘルパー関数（日時変換・パース）
   =============================================================== */

/**
 * 日付文字列と時刻文字列からDateオブジェクトを生成
 * @param {string} dStr - 日付文字列 (YYYY-MM-DD)
 * @param {string} tStr - 時刻文字列 (HH:MM または HH:MM:SS)
 * @returns {Date|null} - Dateオブジェクト、無効な場合はnull
 */
const toDateFromParts = (dStr, tStr) => {
  if (!dStr || !tStr) return null;
  const t = tStr.length === 5 ? `${tStr}:00` : tStr;
  const d = new Date(`${dStr}T${t}`);
  return isNaN(d) ? null : d;
};

/**
 * ISO形式の文字列からDateオブジェクトを生成
 * @param {string} iso - ISO形式の日時文字列
 * @returns {Date|null} - Dateオブジェクト、無効な場合はnull
 */
const toDateFromISO = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d) ? null : d;
};

/* ===============================================================
   ヘルパー関数（期間・時刻表示）
   =============================================================== */

/**
 * イベントオブジェクトから期間文字列を生成
 * 同日: YYYY/MM/DD(曜)　HH：MM～HH：MM
 * 別日: YYYY/MM/DD(曜)～YYYY/MM/DD(曜)　HH：MM～HH：MM
 * @param {Object} ev - イベントオブジェクト
 * @returns {string} - フォーマットされた期間文字列
 */
const formatPeriod = (ev) => {
  if (!ev) return '—';
  const { schedules, times_unified } = ev;
  if (!schedules || schedules.length === 0) return '—';

  const fmt = (t) => String(t || '').slice(0, 5).replace(':', '：');

  const receptPart = (s) => {
    if (!s.recept_start_time || !s.recept_end_time) return '';
    return `（当日受付　${fmt(s.recept_start_time)}～${fmt(s.recept_end_time)}）`;
  };

  if (times_unified) {
    const first = schedules[0];
    const last  = schedules[schedules.length - 1];
    const datePart = schedules.length === 1
      ? fmtYMDw(new Date(first.date))
      : `${fmtYMDw(new Date(first.date))}～${fmtYMDw(new Date(last.date))}`;
    return (
      <div>
        {`${datePart}　${fmt(first.start_time)}～${fmt(first.end_time)}${receptPart(first)}`}
      </div>
    );
  }

  return schedules.map((s, i) => (
    <div key={i}>
      {`${fmtYMDw(new Date(s.date))}　${fmt(s.start_time)}～${fmt(s.end_time)}${receptPart(s)}`}
    </div>
  ));
};

/**
 * ISO日時または "HH:MM(:SS)" から "HH：MM" 形式に変換
 * @param {string|Date} v - 時刻値
 * @returns {string|null} - フォーマットされた時刻文字列
 */
const fmtHMOnlyFromAny = (v) => {
  if (!v) return null;
  
  if (typeof v === 'string') {
    // ISO形式の場合
    if (v.includes('T')) {
      const d = new Date(v);
      if (!isNaN(d)) return `${pad2(d.getHours())}：${pad2(d.getMinutes())}`;
    }
    // "HH:MM" または "HH:MM:SS" 形式の場合
    const m = v.match(/^(\d{1,2}):(\d{2})/);
    if (m) return `${pad2(+m[1])}：${m[2]}`;
    return null;
  }
  
  if (v instanceof Date && !isNaN(v)) {
    return `${pad2(v.getHours())}：${pad2(v.getMinutes())}`;
  }
  
  return null;
};

/**
 * イベントの受付時間レンジを "HH：MM～HH：MM" 形式で生成
 * @param {Object} ev - イベントオブジェクト
 * @returns {string|null} - フォーマットされた受付時間レンジ
 */
const formatReceptionRangeHM = (ev) => {
  const a = fmtHMOnlyFromAny(ev?.recept_start_time);
  const b = fmtHMOnlyFromAny(ev?.recept_end_time);
  
  if (!a && !b) return null;
  if (a && b) return `${a}～${b}`;
  return a || b;
};

/**
 * ISO形式から "YYYY/MM/DD(曜)　HH：MM" 形式に変換
 * @param {string} iso - ISO形式の日時文字列
 * @returns {string|null} - フォーマットされた日時文字列
 */
const fmtYMDHMFromISO = (iso) => {
  const d = toDateFromISO(iso);
  return d ? `${fmtYMDw(d)}　${fmtHMJP(d)}` : null;
};



// DatetimeComboBox: 入力しても選択肢が全件表示されるコンボボックス
function DatetimeComboBox({ value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={wrapRef} className="hev-comboBox">
      <input
        className="hev-input datetimeSelect"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        placeholder={placeholder || '選択または入力してください'}
        autoComplete="off"
      />
      {open && options.length > 0 && (
        <ul className="hev-comboBox__dropdown">
          {options.map((val, i) => (
            <li
              key={i}
              className={`hev-comboBox__item${val === value ? ' hev-comboBox__item--selected' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(val);
                setOpen(false);
              }}
            >
              {val}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function formatmdDate(date) {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${m}/${d}(${JWD[date.getDay()]})`;    // ← JWD に変更
}

// 時刻を「14：30」形式に
function formatTime(date) {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}：${m}`;
}


function generateDatetimeSlots(event, mode) {
  const schedules = event.schedules || [];
  if (schedules.length === 0) return [];

  const intervalMin = mode === '60min' ? 60 : 30;

  const parseHHMM = (timeStr) => {
    if (!timeStr) return null;
    const parts = String(timeStr).split(':');
    return { h: parseInt(parts[0], 10), m: parseInt(parts[1], 10) };
  };

  const slots = [];

  for (const sc of schedules) {
    const day = new Date(sc.date);
    const receptStart = parseHHMM(sc.recept_start_time);
    const receptEnd   = parseHHMM(sc.recept_end_time);
    const evStart     = parseHHMM(sc.start_time);
    const evEnd       = parseHHMM(sc.end_time);

    const fromH = receptStart ? receptStart.h : (evStart ? evStart.h : 9);
    const fromM = receptStart ? receptStart.m : (evStart ? evStart.m : 0);
    const toH   = receptEnd   ? receptEnd.h   : (evEnd   ? evEnd.h   : 17);
    const toM   = receptEnd   ? receptEnd.m   : (evEnd   ? evEnd.m   : 0);

    const slotCur = new Date(day);
    slotCur.setHours(fromH, fromM, 0, 0);

    if (!receptStart) {
      const rem = slotCur.getMinutes() % intervalMin;
      if (rem !== 0) {
        slotCur.setMinutes(slotCur.getMinutes() - rem + intervalMin);
      }
    }

    const slotEnd = new Date(day);
    slotEnd.setHours(toH, toM, 0, 0);

    while (slotCur <= slotEnd) {
      slots.push(new Date(slotCur));
      slotCur.setMinutes(slotCur.getMinutes() + intervalMin);
    }
  }

  return slots;
}


/* ===============================================================
   メインコンポーネント
   =============================================================== */

const GuestEvent = () => {
  const { invite_token } = useParams();

  /* ---------------------------------------------------------------
     State定義
     --------------------------------------------------------------- */
  
  // イベント情報関連
  const [event, setEvent] = useState(null);
  const [my, setMy] = useState(null);          // 参加者情報（自分）
  const [isJoined, setIsJoined] = useState(false);  // 参加済みフラグ

  // UI状態関連
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);    // 参加処理中フラグ
  const [canceling, setCanceling] = useState(false); // キャンセル処理中フラグ

  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [groupSelections, setGroupSelections] = useState({});
  const [groupTexts, setGroupTexts] = useState({});
  /* ---------------------------------------------------------------
     API呼び出し関数
     --------------------------------------------------------------- */
  
  /**
   * イベント詳細を取得
   */
  const fetchDetail = async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await axios.get(
        `${API_URL}/api/enduser/events/token/${invite_token}/`);
      if (r.data?.success) {
        setEvent(r.data.event);
        setMy(r.data.participant);
        setIsJoined(!!r.data.is_participating);
      } else {
        setErr('詳細の取得に失敗しました');
      }
    } catch (e) {
      console.error(e);
      setErr('詳細の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  /**
 * 参加登録を送信
 * @param {'参加'|'不参加'} status
 */
  const submitJoin = async (status) => {
    setJoining(true);
    try {
      const answers = [
        ...Object.entries(groupSelections).map(([gid, typeId]) => ({
          event_group_id: Number(gid),
          event_type_id: typeId ? Number(typeId) : null,
          free_text: null,
        })),
        ...Object.entries(groupTexts).map(([gid, text]) => ({
          event_group_id: Number(gid),
          event_type_id: null,
          free_text: (text || '').trim() || null,
        })),
      ];

      await axios.post(
        `${API_URL}/api/enduser/events/token/${invite_token}/join/`,
        { participant_status: status, group_answers: answers }
      );
      setJoinModalOpen(false);
      setGroupSelections({});   // ← 追加
      setGroupTexts({});        // ← 追加
      setIsEditMode(false);     // ← 追加
      await fetchDetail();
    } catch (e) {
      console.error(e);
      alert(status === '不参加' ? '不参加の登録に失敗しました' : '参加に失敗しました');
    } finally {
      setJoining(false);
    }
  };

  
  /**
   * イベント参加をキャンセル
   */
  const cancel = async () => {
    if (!window.confirm('本当に参加をキャンセルしますか？')) {
      return;
    }
    setCanceling(true);
    try {
      await axios.post(
        `${API_URL}/api/enduser/events/token/${invite_token}/cancel/`);
      await fetchDetail();
    } catch (e) {
      console.error(e);
      alert('キャンセルに失敗しました');
    } finally {
      setCanceling(false);
    }
  };


  /**
 * グループ入力の初期化（既存回答があれば復元）
 */
  const initGroupInputs = (ev, participant) => {
    const selections = {};
    const texts = {};
    (ev?.event_groups || []).forEach(g => {
      const types = g.types || [];
      const isDatetime = (g.description || '').startsWith('日時');
      const isFreeText = !isDatetime && types.length === 1 && types[0].event_type_name === '';

      const prev = (participant?.selections || [])
        .find(s => s.event_group_id === g.event_group_id);
      const prevName = prev?.event_type_name || '';

      if (isDatetime || isFreeText) {
        texts[g.event_group_id] = prevName;
      } else {
        const matched = types.find(t => (t.event_type_name || '') === prevName);
        selections[g.event_group_id] = matched ? String(matched.event_type_id) : '';
      }
    });
    setGroupSelections(selections);
    setGroupTexts(texts);
  };

  /**
   * 参加モーダルを開く
   */
  const openJoinModal = () => {
    // キャンセル済みから再参加する場合は、前回の回答を復元しない
    const isRejoining = my?.participant_status === 'キャンセル' 
                    || my?.participant_status === '不参加';
    initGroupInputs(event, isRejoining ? null : my);
    setIsEditMode(false);
    setJoinModalOpen(true);
  };

  const openEditModal = () => {
    initGroupInputs(event, my);
    setIsEditMode(true);
    setJoinModalOpen(true);
  };

  /* ---------------------------------------------------------------
     画面遷移関数
     --------------------------------------------------------------- */
  
  /**
   * 招待状表示画面を開く
   * @param {string} inviter - 招待トークン
   * @param {string} code - QRコード
   */
  const showInvite = (inviter, code) => {
    const url = `/inviteprint?inviter=${encodeURIComponent(inviter)}&code=${encodeURIComponent(code)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  /**
   * QR表示画面を開く
   * @param {string} inviter - 招待トークン
   * @param {string} code - QRコード
   */
  const showQr = (inviter, code) => {
    const url = `/qrdisplay?inviter=${encodeURIComponent(inviter)}&code=${encodeURIComponent(code)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  /* ---------------------------------------------------------------
     useEffect（副作用処理）
     --------------------------------------------------------------- */
  
/**
 * 参加ボタンを押せるかの判定（必須項目チェック）
 */
  const canSubmitJoin = useMemo(() => {
    if (joining) return false;
    const groups = event?.event_groups || [];
    const invalid = groups.filter(g => {
      if (g.required_category !== 1) return false;
      const types = g.types || [];
      const isDatetime = (g.description || '').startsWith('日時');
      const isFreeText = !isDatetime && types.length === 1 && types[0].event_type_name === '';
      if (isDatetime || isFreeText) {
        return !(groupTexts[g.event_group_id] ?? '').trim();
      }
      return !groupSelections[g.event_group_id];
    });
    return invalid.length === 0;
  }, [joining, event, groupSelections, groupTexts]);


  // 招待トークン変更時にイベント詳細を取得
  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invite_token]);

  /* ---------------------------------------------------------------
     レンダリング関数
     --------------------------------------------------------------- */
  
  /**
   * エラーメッセージを表示
   */
  const renderError = () => (
    err && <div className="error-alert">{err}</div>
  );

  /**
   * イベント基本情報を表示
   */
  const renderEventBasicInfo = () => {
    
    return (
      <>
        {/* タイトル */}
        <div>
          <b>タイトル:</b>{' '}
          {event.event_title}
          {event.event_title_sub ? `（${event.event_title_sub}）` : ''}
        </div>

        {/* 開催期間 */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <b style={{ whiteSpace: 'nowrap' }}>開催期間:</b>
          <div>{formatPeriod(event)}</div>
        </div>
      
        {/* 開催会場 */}
        <div><b>開催会場:</b> {event.event_location || '-'}</div>

        {/* 開催住所（存在する場合のみ） */}
        {event.event_address && (
          <div><b>開催住所:</b> {event.event_address}</div>
        )}

        {/* 定員（存在する場合のみ） */}
        {typeof event.capacity === 'number' && event.capacity > 0 && (
          <div><b>定員:</b> {event.capacity}</div>
        )}
      </>
    );
  };

  /**
   * イベント説明・補足情報を表示
   */
  const renderEventDescription = () => (
    <>
      {/* 説明（存在する場合のみ） */}
      {event.event_desc && (
        <div style={{ marginTop: 8 }}>
          <b>説明:</b>
          <div style={{ whiteSpace: 'pre-wrap' }}>{event.event_desc}</div>
        </div>
      )}

      {/* イベントURL（存在する場合のみ） */}
      {event.event_url && (
        <div>
          <b>イベントURL:</b>{' '}
          <a href={event.event_url} target="_blank" rel="noopener noreferrer">
            {event.event_url}
          </a>
        </div>
      )}

      {/* 参加募集締切日時（存在する場合のみ） */}
      {event.deadline && (
        <div><b>参加募集締切日時:</b> {fmtYMDHMFromISO(event.deadline)}</div>
      )}

      {/* 運営事務局（存在する場合のみ） */}
      {event.management_office && (
        <div><b>運営事務局:</b> {event.management_office}</div>
      )}

      {/* ご案内・注意事項（存在する場合のみ） */}
      {event.information_notes && (
        <div style={{ marginTop: 8 }}>
          <b>ご案内・注意事項:</b>
          <div style={{ whiteSpace: 'pre-wrap' }}>{event.information_notes}</div>
        </div>
      )}
    </>
  );

  /**
   * イベント情報セクション全体を表示
   */
  const renderEventInfo = () => (
    <div className="eventInfo">
      <span className="eventInfo__eyebrow">イベント詳細</span>
      {renderEventBasicInfo()}
      {renderEventDescription()}
    </div>
  );

  /**
 * 参加モーダル内のグループ入力フィールドを描画
 */
  const renderGroupField = (g) => {
    const gid = g.event_group_id;
    const types = g.types || [];
    const isFreeText = types.length === 1 && types[0].event_type_name === '';
    const isDatetime = (g.description || '').startsWith('日時');
    const label = g.description ? `${g.group_name}（${g.description}）` : g.group_name;
    const required = g.required_category === 1;

    // 日時コンボボックス
    if (isDatetime) {
      const mode = (g.description || '').includes(':') ? g.description.split(':')[1] : 'ampm';
      let options = [];
      if (mode === 'ampm') {
        const schedules = event?.schedules || [];

        options = schedules.flatMap(s => {
          const date = new Date(s.date);
          const dateStr = formatmdDate(date); // 04/22(水) みたいな

          return [
            `${dateStr} 午前`,
            `${dateStr} 午後`
          ];
        });
      } else {
        options = generateDatetimeSlots(event, mode)
          .map(slot => `${formatmdDate(slot)} ${formatTime(slot)}`);
      }
      return (
        <label className="hev-field" key={gid}>
          <span className="hev-fieldLabel">{g.group_name}{required ? ' *' : ''}</span>
          <DatetimeComboBox
            value={groupTexts[gid] ?? ''}
            onChange={(val) => setGroupTexts(s => ({ ...s, [gid]: val }))}
            options={options}
          />
        </label>
      );
    }

    // 自由入力
    if (isFreeText) {
      const maxLen = (g.display_id === 2 || g.display_id === 3) ? 13 : undefined;
      return (
        <label className="hev-field" key={gid}>
          <span className="hev-fieldLabel">
            {label}{required ? ' *' : ''}
            {maxLen && (
              <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: 8, fontWeight: 'normal' }}>
                {(groupTexts[gid] ?? '').length} / {maxLen}
              </span>
            )}
          </span>
          <input
            className="hev-input"
            type="text"
            value={groupTexts[gid] ?? ''}
            onChange={(e) => setGroupTexts(s => ({ ...s, [gid]: e.target.value }))}
            placeholder={maxLen ? `入力してください（${maxLen}文字以内）` : '入力してください'}
            maxLength={maxLen}
          />
        </label>
      );
    }

    // 選択肢（select）
    return (
      <label className="hev-field" key={gid}>
        <span className="hev-fieldLabel">{label}{required ? ' *' : ''}</span>
        <select
          className="hev-input"
          value={groupSelections[gid] ?? ''}
          onChange={(e) => setGroupSelections(s => ({ ...s, [gid]: e.target.value }))}
        >
          <option value="">選択してください</option>
          {types.map(t => (
            <option key={t.event_type_id} value={t.event_type_id}>
              {t.event_type_name ?? '（名称未設定）'}
              {t.event_type_price > 0 ? `（${t.event_type_price.toLocaleString()}円）` : ''}
            </option>
          ))}
        </select>
      </label>
    );
  };


  const closeJoinModal = () => {
    setJoinModalOpen(false);
    setGroupSelections({});
    setGroupTexts({});
    setIsEditMode(false);
  };

  /**
   * 参加モーダル
   */
  const renderJoinModal = () => (
    joinModalOpen && (
      <div className="hev-modalOverlay">
        <div className="hev-modalBody" onClick={e => e.stopPropagation()}>
          <div className="hev-modalHead">
            <div className="hev-modalTitle">
              {isEditMode ? '回答内容を編集' : '参加登録'}
            </div>
            <div className="hev-spacer" />
            <button className="hev-btn" onClick={closeJoinModal}>閉じる</button>
          </div>

          <div className="hev-modalContent">
            <div className="hev-formGrid">
              {(event?.event_groups || []).length === 0 ? (
                <div style={{ color: '#6b7280' }}>
                  このイベントに入力項目はありません。そのまま「参加する」を押してください。
                </div>
              ) : (
                event.event_groups.map(renderGroupField)
              )}
            </div>

            <div className="hev-modalFoot">
              <button className="hev-btn" onClick={closeJoinModal}>キャンセル</button>
              <button
                className={`hev-btn hev-btn--primary${!canSubmitJoin ? ' is-disabled' : ''}`}
                disabled={!canSubmitJoin}
                onClick={() => submitJoin('参加')}
              >
                {joining
                  ? (isEditMode ? '更新中…' : '参加処理中…')
                  : (isEditMode ? '更新する' : '参加する')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  );

  /**
   * 操作ボタン（参加/キャンセル）を表示
   */
const renderOperationButtons = () => {
  const vis = event?.visibility;
  const status = my?.participant_status;
  const hasGroups = (event?.event_groups || []).length > 0;
  const canAnswerDecline = vis === '全体公開(出欠表示)' || vis === '招待制';

  const statusLabel = (() => {
    if (status === '参加')       return { text: '参加で回答済み', className: 'is-joined' };
    if (status === '不参加')     return { text: '不参加で回答済み', className: 'is-declined' };
    if (status === 'キャンセル') return { text: 'キャンセルで回答済み', className: 'is-cancel' };
    return null;
  })();

  const renderLabel = () => (
    statusLabel && <span className={`statusBadge ${statusLabel.className}`}>{statusLabel.text}</span>
  );

  // 参加済み
  if (status === '参加') {
    return (
      <div className="opsRow">
        {renderLabel()}
        <div className="opsRow__buttons">
          {hasGroups && (
            <button className="btn" onClick={openEditModal} disabled={joining || loading}>
              編集する
            </button>
          )}
          <button className="btn danger" onClick={cancel} disabled={canceling || loading}>
            {canceling ? 'キャンセル処理中…' : 'キャンセルする'}
          </button>
        </div>
      </div>
    );
  }

  // 不参加済み
  if (status === '不参加') {
    return (
      <div className="opsRow">
        {renderLabel()}
        <div className="opsRow__buttons">
          <button
            className="btn primary"
            onClick={() => hasGroups ? openJoinModal() : submitJoin('参加')}
            disabled={joining || loading}
          >
            {joining ? '送信中…' : '参加する'}
          </button>

          {canAnswerDecline && (
            <button className="btn" disabled>
              不参加
            </button>
          )}
        </div>
      </div>
    );
  }

  // キャンセル済み
  if (status === 'キャンセル') {
    return (
      <div className="opsRow">
        {renderLabel()}
        <div className="opsRow__buttons">
          <button
            className="btn primary"
            onClick={() => hasGroups ? openJoinModal() : submitJoin('参加')}
            disabled={joining || loading}
          >
            {joining ? '送信中…' : '参加する'}
          </button>

          {canAnswerDecline && (
            <button
              className="btn"
              onClick={() => submitJoin('不参加')}
              disabled={joining || loading}
            >
              不参加
            </button>
          )}
        </div>
      </div>
    );
  }

  // 未回答
  if (canAnswerDecline) {
    return (
      <div className="opsRow">
        <div className="opsRow__buttons">
          <button
            className="btn primary"
            onClick={() => hasGroups ? openJoinModal() : submitJoin('参加')}
            disabled={joining || loading}
          >
            {joining ? '送信中…' : '参加'}
          </button>
          <button
            className="btn"
            onClick={() => submitJoin('不参加')}
            disabled={joining || loading}
          >
            不参加
          </button>
        </div>
      </div>
    );
  }

  // 全体公開は従来どおり参加のみ
  return (
    <div className="opsRow">
      {renderLabel()}
      <button
        className="btn primary"
        onClick={() => hasGroups ? openJoinModal() : submitJoin('参加')}
        disabled={joining || loading}
      >
        {joining ? '参加処理中…' : '参加する'}
      </button>
    </div>
  );
};

  /**
   * 参加者情報セクションを表示（参加済みの場合のみ）
   */
  const renderMyInfo = () => {
    if (!isJoined || !my) return null;

    const selections = my.selections || [];
    const hasGroups = (event?.event_groups || []).length > 0;

    // QR/招待状ボタン部分
    const renderQrButton = () => {
      if (event.checkin_type === 'WEB表示' && my.qr_code) {
        return (
          <button className="btn" onClick={() => showQr(invite_token, my.qr_code)}>
            QRを表示
          </button>
        );
      }
      if (event.checkin_type === '印刷' && my.qr_code) {
        return (
          <button className="btn" onClick={() => showInvite(invite_token, my.qr_code)}>
            招待状を表示
          </button>
        );
      }
      return null;
    };

    const qrButton = renderQrButton();

    return (
      <div className="myInfo">
        <div className="myInfo__header">
          <h3 className="myInfo__title">あなたの参加情報</h3>
          {qrButton && <div className="myInfo__qrArea">{qrButton}</div>}
        </div>

        {!qrButton && <p className="myInfo__plain">参加済みです。</p>}

        {/* 入力項目がある場合のみ、回答内容を表示 */}
        {hasGroups && (
          <div className="myAnswers">
            <div className="myAnswers__head">
              <h4 className="myAnswers__heading">回答内容</h4>
            </div>
            <dl className="myAnswers__list">
              {event.event_groups.map((g) => {
                const answer = selections.find(s => s.event_group_id === g.event_group_id);
                const hasAnswer = !!answer?.event_type_name;
                return (
                  <div key={g.event_group_id} className="myAnswers__row">
                    <dt className="myAnswers__dt">
                      {g.group_name}
                      {g.required_category === 1 && (
                        <span className="myAnswers__required">*</span>
                      )}
                    </dt>
                    <dd className="myAnswers__dd">
                      {hasAnswer ? (
                        answer.event_type_name
                      ) : (
                        <span className="myAnswers__empty">未回答</span>
                      )}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        )}
      </div>
    );
  };

  /**
   * イベント詳細コンテンツ全体を表示
   */
  const renderEventContent = () => {
    if (!event) return null;
    
    return (
      <>
        {/* イベント情報 */}
        {renderEventInfo()}
        
        {/* 操作ボタン */}
        {renderOperationButtons()}
        {renderJoinModal()} 
        {/* 参加者情報（参加済みの場合のみ） */}
        {renderMyInfo()}
      </>
    );
  };

  /* ---------------------------------------------------------------
     メインレンダリング
     --------------------------------------------------------------- */
  return (
    <div className="guestEventDetail">
      {/* エラー表示 */}
      {renderError()}
      
      {/* イベント詳細コンテンツ */}
      {renderEventContent()}
    </div>
  );
};

export default GuestEvent;